import { useCallback } from "react";
import PropTypes from "prop-types";
import { useDropzone } from "react-dropzone";
import { Progress } from "reactstrap";
import { toast } from "react-toastify";
import {
  PRODUCT_IMAGE_ACCEPTED_TYPES,
  PRODUCT_IMAGE_MAX_SIZE_BYTES,
  PRODUCT_MAX_IMAGES,
} from "../../../constants/promotedProducts";
import { normalizeBackendFileUrl } from "../../../utils/backendApi";

/**
 * Image picker used by the create/edit form.
 *
 * Handles two kinds of entries side by side:
 *  - pending: files picked locally, not uploaded yet (have `file` + `preview`)
 *  - saved:   images already on the server (have `id` + `image`)
 */
const ProductImagesUploader = ({
  pendingImages,
  savedImages,
  onAddFiles,
  onRemovePending,
  onRemoveSaved,
  onSetCoverPending,
  onSetCoverSaved,
  uploadProgress,
  disabled,
}) => {
  const totalCount = pendingImages.length + savedImages.length;

  const onDrop = useCallback(
    (acceptedFiles, fileRejections) => {
      fileRejections.forEach((rejection) => {
        const tooLarge = rejection.errors.some(
          (e) => e.code === "file-too-large"
        );
        toast.error(
          tooLarge
            ? `« ${rejection.file.name} » dépasse 5 Mo.`
            : `« ${rejection.file.name} » n'est pas une image acceptée (jpg, png, webp).`,
          { autoClose: 4000 }
        );
      });

      if (acceptedFiles.length === 0) return;

      const remaining = PRODUCT_MAX_IMAGES - totalCount;
      if (remaining <= 0) {
        toast.error(`Maximum ${PRODUCT_MAX_IMAGES} images par produit.`, {
          autoClose: 4000,
        });
        return;
      }

      if (acceptedFiles.length > remaining) {
        toast.warn(
          `Seules ${remaining} image(s) supplémentaires peuvent être ajoutées.`,
          { autoClose: 4000 }
        );
      }

      onAddFiles(acceptedFiles.slice(0, remaining));
    },
    [onAddFiles, totalCount]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    // react-dropzone is pinned to ^12, whose `accept` is a comma-separated
    // string; the v14 `{ mime: [] }` object makes attr-accept throw on every file.
    accept: PRODUCT_IMAGE_ACCEPTED_TYPES.join(","),
    // One code path — the <input accept> dialog — for every browser and for the
    // E2E suite, instead of the File System Access API on Chrome only.
    useFsAccessApi: false,
    maxSize: PRODUCT_IMAGE_MAX_SIZE_BYTES,
    disabled: disabled || totalCount >= PRODUCT_MAX_IMAGES,
  });

  const renderTile = (key, src, isCover, onRemove, onSetCover, progress) => (
    <div key={key} className="position-relative m-1" style={{ width: 110 }}>
      <div
        className="border rounded overflow-hidden position-relative"
        style={{
          height: 110,
          borderColor: isCover ? "#0ab39c" : undefined,
          borderWidth: isCover ? 2 : 1,
        }}
      >
        <img
          src={src}
          alt="Produit"
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
        {isCover && (
          <span
            className="badge bg-success position-absolute"
            style={{ top: 4, left: 4 }}
          >
            <i className="ri-star-fill" /> Couverture
          </span>
        )}
      </div>

      {typeof progress === "number" && progress < 100 && (
        <Progress value={progress} className="mt-1" style={{ height: 4 }} />
      )}

      <div className="d-flex justify-content-between mt-1">
        <button
          type="button"
          className="btn btn-sm btn-soft-danger py-0 px-1"
          onClick={onRemove}
          disabled={disabled}
          title="Supprimer"
        >
          <i className="ri-close-line" />
        </button>
        {!isCover && (
          <button
            type="button"
            className="btn btn-sm btn-soft-success py-0 px-1"
            onClick={onSetCover}
            disabled={disabled}
            title="Définir comme couverture"
          >
            <i className="ri-star-line" />
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h5 className="fs-14 mb-0">Images du produit</h5>
        <small className="text-muted">
          {totalCount}/{PRODUCT_MAX_IMAGES} — max 5 Mo, jpg/png/webp
        </small>
      </div>

      <div className="d-flex flex-wrap">
        {savedImages.map((img) =>
          renderTile(
            `saved-${img.id}`,
            normalizeBackendFileUrl(img.image),
            img.is_cover,
            () => onRemoveSaved(img),
            () => onSetCoverSaved(img),
            undefined
          )
        )}

        {pendingImages.map((img, index) =>
          renderTile(
            `pending-${img.uid}`,
            img.preview,
            img.isCover,
            () => onRemovePending(img),
            () => onSetCoverPending(img),
            uploadProgress?.[img.uid]
          )
        )}
      </div>

      {totalCount < PRODUCT_MAX_IMAGES && (
        <div
          {...getRootProps()}
          className="dropzone border rounded text-center p-4 mt-2"
          style={{
            cursor: disabled ? "not-allowed" : "pointer",
            borderStyle: "dashed",
            backgroundColor: isDragActive ? "#f3f6f9" : undefined,
          }}
        >
          <input {...getInputProps()} />
          <i className="display-6 text-muted ri-upload-cloud-2-fill" />
          <h6 className="mt-2 mb-0">
            {isDragActive
              ? "Déposez les images ici"
              : "Glissez-déposez vos images ou cliquez pour les sélectionner"}
          </h6>
        </div>
      )}

      {totalCount === 0 && (
        <small className="text-danger d-block mt-2">
          Au moins une image est nécessaire pour pouvoir valider le produit.
        </small>
      )}
    </div>
  );
};

ProductImagesUploader.propTypes = {
  pendingImages: PropTypes.array,
  savedImages: PropTypes.array,
  onAddFiles: PropTypes.func.isRequired,
  onRemovePending: PropTypes.func.isRequired,
  onRemoveSaved: PropTypes.func,
  onSetCoverPending: PropTypes.func.isRequired,
  onSetCoverSaved: PropTypes.func,
  uploadProgress: PropTypes.object,
  disabled: PropTypes.bool,
};

ProductImagesUploader.defaultProps = {
  pendingImages: [],
  savedImages: [],
  uploadProgress: {},
  disabled: false,
};

export default ProductImagesUploader;
